// Application configuration with environment-based settings
export interface AppConfig {
  // API Configuration
  api: {
    baseUrl: string;
    timeout: number;
    retryAttempts: number;
    retryDelay: number;
  };
  
  // Chat Configuration
  chat: {
    maxMessageLength: number;
    streamingEnabled: boolean;
    typingIndicatorDelay: number;
    sessionTimeout: number;
    maxConversationHistory: number;
  };
  
  // Performance Configuration
  performance: {
    cacheSize: number;
    debounceDelay: number;
    lazyLoadThreshold: number;
    virtualScrollThreshold: number;
  };
  
  // Feature Flags
  features: {
    offlineSupport: boolean;
    voiceInput: boolean;
    darkMode: boolean;
    analytics: boolean;
    experimentalA2A: boolean;
    // Product Searchlight "Collaborator" mode. Build-time gated so a deploy
    // build can ship with it switched off (set REACT_APP_ENABLE_PRODUCT_COLLABORATOR=false).
    productCollaborator: boolean;
  };
  
  // UI Configuration
  ui: {
    animationDuration: number;
    toastDuration: number;
    autoScrollDelay: number;
    maxImageSize: number;
  };
  
  // Development flags
  development: {
    debug: boolean;
    mockMode: boolean;
    showPerformanceMetrics: boolean;
    verboseLogging: boolean;
  };
}

// Default configuration
const defaultConfig: AppConfig = {
  api: {
    // Always use local API in development (ignore REACT_APP_API_URL)
    baseUrl: process.env.NODE_ENV === 'development'
      ? ''
      : (process.env.REACT_APP_API_URL || ''),
    timeout: 30000,
    retryAttempts: 3,
    retryDelay: 1000,
  },
  
  chat: {
    maxMessageLength: 4000,
    streamingEnabled: true,
    typingIndicatorDelay: 500,
    sessionTimeout: 30 * 60 * 1000, // 30 minutes
    maxConversationHistory: 100,
  },
  
  performance: {
    cacheSize: 100, // Number of cached responses
    debounceDelay: 300,
    lazyLoadThreshold: 3, // Number of messages before lazy loading
    virtualScrollThreshold: 50, // Number of messages before virtual scroll
  },
  
  features: {
    offlineSupport: true,
    voiceInput: false,
    darkMode: true,
    analytics: process.env.NODE_ENV === 'production',
    experimentalA2A: process.env.REACT_APP_ENABLE_A2A === 'true',
    // Switched OFF for this deploy build; opt back in via REACT_APP_ENABLE_PRODUCT_COLLABORATOR=true.
    productCollaborator: process.env.REACT_APP_ENABLE_PRODUCT_COLLABORATOR === 'true',
  },
  
  ui: {
    animationDuration: 300,
    toastDuration: 5000,
    autoScrollDelay: 100,
    maxImageSize: 5 * 1024 * 1024, // 5MB
  },
  
  development: {
    debug: process.env.NODE_ENV === 'development',
    mockMode: process.env.REACT_APP_MOCK_MODE === 'true',
    showPerformanceMetrics: process.env.REACT_APP_SHOW_METRICS === 'true',
    verboseLogging: process.env.REACT_APP_VERBOSE_LOGGING === 'true',
  },
};

// Environment-specific overrides
const getEnvironmentConfig = (): Partial<AppConfig> => {
  const env = process.env.NODE_ENV;
  
  switch (env) {
    case 'production':
      return {
        api: {
          ...defaultConfig.api,
          timeout: 60000, // Longer timeout for production
        },
        chat: {
          ...defaultConfig.chat,
          maxConversationHistory: 200, // More history in production
        },
        development: {
          ...defaultConfig.development,
          debug: false,
          mockMode: false,
          showPerformanceMetrics: false,
          verboseLogging: false,
        },
      };
      
    case 'test':
      return {
        api: {
          ...defaultConfig.api,
          timeout: 5000, // Short timeout for tests
          retryAttempts: 1,
        },
        features: {
          ...defaultConfig.features,
          analytics: false,
        },
        development: {
          ...defaultConfig.development,
          debug: true,
          mockMode: true,
        },
      };
      
    default: // development
      return {
        development: {
          ...defaultConfig.development,
          debug: true,
          verboseLogging: true,
        },
      };
  }
};

// Merge configurations
const createConfig = (): AppConfig => {
  const envConfig = getEnvironmentConfig();
  
  return {
    api: { ...defaultConfig.api, ...envConfig.api },
    chat: { ...defaultConfig.chat, ...envConfig.chat },
    performance: { ...defaultConfig.performance, ...envConfig.performance },
    features: { ...defaultConfig.features, ...envConfig.features },
    ui: { ...defaultConfig.ui, ...envConfig.ui },
    development: { ...defaultConfig.development, ...envConfig.development },
  };
};

// Export the final configuration
export const appConfig = createConfig();

// Configuration utilities
export const isFeatureEnabled = (feature: keyof AppConfig['features']): boolean => {
  return appConfig.features[feature];
};

export const isDevelopment = (): boolean => {
  return appConfig.development.debug;
};

export const isProduction = (): boolean => {
  return process.env.NODE_ENV === 'production';
};

// Runtime configuration updates (for feature flags)
export const updateFeatureFlag = (feature: keyof AppConfig['features'], enabled: boolean): void => {
  appConfig.features[feature] = enabled;
  
  // Persist to localStorage if available
  if (typeof window !== 'undefined' && window.localStorage) {
    const featureFlags = JSON.parse(localStorage.getItem('unichat_features') || '{}');
    featureFlags[feature] = enabled;
    localStorage.setItem('unichat_features', JSON.stringify(featureFlags));
  }
};

// Load feature flags from localStorage on startup
export const loadFeatureFlags = (): void => {
  if (typeof window !== 'undefined' && window.localStorage) {
    const saved = localStorage.getItem('unichat_features');
    if (saved) {
      try {
        const featureFlags = JSON.parse(saved);
        Object.assign(appConfig.features, featureFlags);
      } catch (error) {
        console.warn('Failed to load feature flags from localStorage:', error);
      }
    }
  }
};

// Initialize configuration
loadFeatureFlags(); 